import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex, Badge, Text } from '@mantine/core';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { EMPTY_CALLBACK } from '@dealstack/shared/index';
import { DropdownInput } from '@dealstack/frontend/shared/components/DropdownInput/DropdownInput';
import { VALUATION_CARD_ACTIONS } from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/ValuationCard/constants';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import {
	StyledDealCard,
	StyledFlexColumn,
	StyledFlexRow,
} from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/ValuationCard/styled';
import { DateTime } from 'luxon';
import { ValuationWithClassifierRes } from '@dealstack/shared/lib/generated';
import {
	makeComplexDataTestId,
	VALUATION_ELEMENT_ID,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	to: string;
	className?: string;
	valuationCardDetails: ValuationWithClassifierRes;
	deleteValuation: (params: { valuationId: string }) => void;
	duplicateValuation: (params: { valuationId: string }) => void;
}

export const ValuationCard = ({
	className,
	to,
	valuationCardDetails: dealCardDetails,
	deleteValuation,
	duplicateValuation,
}: Props) => {
	const tValuations = useTranslationValuations();

	return (
		<StyledDealCard
			data-testid={makeComplexDataTestId(
				VALUATION_ELEMENT_ID.VALUATION_CARD,
				dealCardDetails.name
			)}
			className={className}
			to={to}
		>
			<Flex direction="column" gap="1rem">
				<Flex direction="row" justify="space-between">
					<StyledFlexColumn>
						<TextV2 variant="sans/25/200">{dealCardDetails.name}</TextV2>
						<TextV2 variant="sans/17/200">
							{DateTime.fromISO(dealCardDetails.valuationDate, {
								zone: 'utc',
							}).toFormat('dd MMM yyyy')}
						</TextV2>
					</StyledFlexColumn>
					<StyledFlexColumn
						data-testid={VALUATION_ELEMENT_ID.VALUATION_DROPDOWN_BTN}
						onClickCapture={(e) => e.preventDefault()}
					>
						<DropdownInput
							value={tValuations('deal_card.valuation_actions')}
							options={VALUATION_CARD_ACTIONS.map(({ label, value, icon }) => ({
								label: tValuations(label),
								value,
								icon,
							}))}
							onChange={(
								valuationAction: (typeof VALUATION_CARD_ACTIONS)[number]['value']
							) => {
								if (valuationAction === 'DELETE') {
									deleteValuation({ valuationId: dealCardDetails.id });
								} else if (valuationAction === 'DUPLICATE') {
									duplicateValuation({
										valuationId: dealCardDetails.id,
									});
								}
							}}
							icon={
								<Icon
									onClick={EMPTY_CALLBACK}
									variant="ellipsis-horizontal"
									size="lg"
									className="m-1"
								/>
							}
						/>
					</StyledFlexColumn>
				</Flex>

				<StyledFlexRow>
					<TextV2 variant="sans/14/200">{tValuations('deal_card.last_updated')}</TextV2>
					<TextV2 variant="sans/14/200">
						{DateTime.fromISO(dealCardDetails.updatedAt, {
							zone: 'utc',
						}).toFormat('dd MMM yyyy')}
					</TextV2>
				</StyledFlexRow>

				<Divider />

				<Flex direction="row" wrap="wrap">
					{dealCardDetails.classifiers.map((classifier) => (
						<Badge
							key={classifier.id}
							size="sm"
							variant="outline"
							color="gray.4"
							style={{ padding: '0.25rem 0.5rem' }}
						>
							<Text
								data-testid={makeComplexDataTestId(
									VALUATION_ELEMENT_ID.VALUATION_TAG_CARD,
									classifier.name
								)}
								size="0.75rem"
								fw={400}
								lh="1rem"
								c="dark.4"
							>
								{tValuations(`classifier.${classifier.id}`)}
							</Text>
						</Badge>
					))}
				</Flex>
			</Flex>
		</StyledDealCard>
	);
};
